import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useAuth } from './AuthContext';
import {
  AppNotification,
  fetchManagerFeed,
  fetchManagerUnreadCount,
  markRead as apiMarkRead,
  markAllRead as apiMarkAllRead,
  dismissNotification as apiDismiss,
} from '../services/notificationsService';

interface NotificationsContextValue {
  items: AppNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const isAuthed = !!user?.managementUser;

  // feat-191-evaluator-role — HYGIENE, not a gate. The bell is hidden for
  // evaluators in ScorecardNavigation, but this provider is mounted app-wide in
  // App.tsx, so without this the feed + unread-count requests still fired on
  // every evaluation page: an icon hidden while its network calls continue is the
  // same presentation-mistaken-for-a-gate mistake one layer down. The endpoint is
  // self-scoped to the caller's own management row, so nothing here is a security
  // boundary — it is two requests nobody will ever see the result of.
  //
  // Deliberately scoped to the AUTOMATIC load only. `refresh()` still works, so
  // an evaluator who reaches /restaurant/notifications by URL (it is
  // ProtectedRoute-gated, so they can) sees an empty list with a working Refresh
  // button rather than a permanently false empty state.
  const isEvaluator = React.useMemo(() => {
    const raw = user?.managementUser?.role;
    if (!raw) return false;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() === 'Evaluator';
  }, [user?.managementUser?.role]);

  const shouldAutoLoad = isAuthed && !isEvaluator;

  const refresh = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true);
    setError(null);
    try {
      const feed = await fetchManagerFeed({ limit: 20 });
      setItems(feed.items);
      setNextCursor(feed.next_cursor);
      const count = await fetchManagerUnreadCount();
      setUnreadCount(count);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loading) return;
    setLoading(true);
    try {
      const feed = await fetchManagerFeed({ limit: 20, cursor: nextCursor });
      setItems((prev) => [...prev, ...feed.items]);
      setNextCursor(feed.next_cursor);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [nextCursor, loading]);

  const markRead = useCallback(async (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await apiMarkRead(id);
    } catch {
      // tolerate; server reconciles on next refresh
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    setUnreadCount(0);
    try {
      await apiMarkAllRead();
    } catch {}
  }, []);

  const dismiss = useCallback(async (id: string) => {
    setItems((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.read_at) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      return prev.filter((n) => n.id !== id);
    });
    try {
      await apiDismiss(id);
    } catch {}
  }, []);

  useEffect(() => {
    if (shouldAutoLoad) {
      refresh();
    } else {
      setItems([]);
      setUnreadCount(0);
      setNextCursor(null);
    }
  }, [shouldAutoLoad, refresh]);

  const value: NotificationsContextValue = {
    items,
    unreadCount,
    loading,
    error,
    hasMore: !!nextCursor,
    refresh,
    loadMore,
    markRead,
    markAllRead,
    dismiss,
  };

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}
